/**
 * AcceptInvite — the screen an invited person lands on.
 *
 * Reached via `?invite=<token>` (see `inviteUrl` in lib/inviteLinks for why
 * a query param rather than a path). Shown BEFORE the auth gate, because the
 * whole point is that this person does not have an account they can log into
 * yet.
 *
 * The token is checked before the form renders, so somebody does not type a
 * password into a form that then tells them the link expired — that is the one
 * piece of information they needed before they started.
 *
 * On success the server signs them in directly. They have just proved
 * possession of the invite AND chosen the credential, so bouncing them to a
 * login form would verify nothing and only add a step to get lost in.
 */
import React, { useEffect, useState } from 'react';
import { Check, Loader2, ShieldCheck } from 'lucide-react';
import client from '@/api/client';

interface InviteCheck {
  valid: boolean;
  email?: string | null;
  full_name?: string | null;
  role?: string | null;
  organization?: string | null;
  expires_at?: string | null;
}

/** Matches the server floor in `schemas/user.InviteRedeem`. */
const MIN_PASSWORD = 12;

interface AcceptInviteProps {
  token: string;
  onSuccess: () => void;
}

export const AcceptInvite: React.FC<AcceptInviteProps> = ({ token, onSuccess }) => {
  const [check, setCheck] = useState<InviteCheck | null>(null);
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    client
      .get<InviteCheck>(`/auth/invite/${encodeURIComponent(token)}`)
      .then((r) => !cancelled && setCheck(r.data))
      .catch(() => !cancelled && setCheck({ valid: false }));
    return () => {
      cancelled = true;
    };
  }, [token]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password !== confirm) {
      setError('The two passwords do not match.');
      return;
    }
    if (password.length < MIN_PASSWORD) {
      setError(`Use at least ${MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const res = await client.post('/auth/invite/redeem', { token, password });
      // Same storage key the normal login path uses, so the app boots
      // authenticated without a second round-trip.
      localStorage.setItem('access_token', res.data.access_token);
      // Drop the token from the URL before handing off. It is spent, but a
      // spent invite sitting in browser history and referrer headers is
      // needless exposure.
      window.history.replaceState({}, '', window.location.pathname);
      onSuccess();
    } catch (err: any) {
      setError(
        err?.response?.data?.detail ??
          'Could not set your password. Ask for a new invite link.',
      );
    } finally {
      setBusy(false);
    }
  };

  if (check === null) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg-base">
        <Loader2 className="w-6 h-6 animate-spin text-fg-muted" />
      </div>
    );
  }

  if (!check.valid) {
    return (
      <div className="min-h-screen grid place-items-center bg-bg-base px-6">
        <div className="max-w-md text-center">
          <h1 className="text-xl font-bold text-fg-primary mb-2">
            This invite link doesn&rsquo;t work
          </h1>
          <p className="text-sm text-fg-muted">
            It may have already been used, been cancelled, or expired. Ask
            whoever invited you to send a new one.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen grid place-items-center bg-bg-base px-6 py-12">
      <div className="w-full max-w-md">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-5 h-5 text-accent-fg" />
          <span className="text-[11px] font-bold uppercase tracking-wider text-fg-muted">
            Set your password
          </span>
        </div>

        <h1 className="text-2xl font-bold text-fg-primary mb-1">
          Welcome{check.full_name ? `, ${check.full_name.split(' ')[0]}` : ''}
        </h1>
        <p className="text-sm text-fg-muted mb-6">
          You&rsquo;re joining{' '}
          <strong className="text-fg-secondary">{check.organization}</strong>
          {check.role && (
            <>
              {' '}
              as{' '}
              <strong className="text-fg-secondary">
                {check.role.replace(/_/g, ' ')}
              </strong>
            </>
          )}
          . Choose a password and you&rsquo;re in.
        </p>

        <form onSubmit={submit} className="space-y-4">
          <div>
            <label className="block text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1.5">
              Email
            </label>
            <input
              className="w-full bg-bg-elevated border border-border-base rounded px-3 py-2 text-sm text-fg-muted"
              value={check.email ?? ''}
              disabled
              readOnly
            />
          </div>

          <div>
            <label
              htmlFor="invite-password"
              className="block text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1.5"
            >
              New password
            </label>
            <input
              id="invite-password"
              type="password"
              autoComplete="new-password"
              className="w-full bg-bg-raised border border-border-subtle rounded px-3 py-2 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
            <p className="mt-1 text-xs text-fg-muted">
              At least {MIN_PASSWORD} characters.
            </p>
          </div>

          <div>
            <label
              htmlFor="invite-confirm"
              className="block text-[11px] font-bold uppercase tracking-wider text-fg-muted mb-1.5"
            >
              Confirm password
            </label>
            <input
              id="invite-confirm"
              type="password"
              autoComplete="new-password"
              className="w-full bg-bg-raised border border-border-subtle rounded px-3 py-2 text-sm text-fg-primary focus:outline-none focus:ring-2 focus:ring-accent"
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              required
            />
          </div>

          {error && (
            <div className="p-3 rounded border border-status-danger text-status-danger text-sm">
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            className="w-full inline-flex items-center justify-center gap-2 bg-accent hover:bg-accent-hover text-white text-sm font-bold rounded px-4 py-2.5 disabled:opacity-50"
          >
            {busy ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Check className="w-4 h-4" />
            )}
            Set password and sign in
          </button>
        </form>
      </div>
    </div>
  );
};

export default AcceptInvite;
